import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSprint, useStartSprint } from '@/hooks/use-sprints';
import { formatDate, parseDateOnly, toDateParam } from '@/lib/date';
import { addDays } from 'date-fns';
import { Play } from 'lucide-react';
import { useState, useEffect } from 'react';

interface StartSprintDialogProps {
  sprintId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DURATION_OPTIONS = [
  { value: '7', label: '1 week' },
  { value: '14', label: '2 weeks' },
  { value: '21', label: '3 weeks' },
  { value: '28', label: '4 weeks' },
  { value: 'custom', label: 'Custom' },
];

export function StartSprintDialog({
  sprintId,
  projectId,
  open,
  onOpenChange,
}: StartSprintDialogProps) {
  const { data: sprint } = useSprint(sprintId);
  const startMutation = useStartSprint(projectId);

  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [durationPreset, setDurationPreset] = useState('14');
  const [customDays, setCustomDays] = useState('14');
  const [startDate, setStartDate] = useState(toDateParam(new Date()) ?? '');

  useEffect(() => {
    if (sprint) {
      setName(sprint.name);
      setGoal(sprint.goal ?? '');
    }
  }, [sprint]);

  const durationDays =
    durationPreset === 'custom' ? parseInt(customDays) || 14 : parseInt(durationPreset);

  const endDate = formatDate(
    toDateParam(addDays(parseDateOnly(startDate), durationDays)),
    'short'
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startMutation.mutate(
      {
        id: sprintId,
        durationDays,
        startDate,
        name: name !== sprint?.name ? name : undefined,
        goal: goal !== sprint?.goal ? goal : undefined,
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-4 w-4 text-green-500" />
              Start Sprint
            </DialogTitle>
            <DialogDescription>
              {sprint?.taskCount ?? 0} tasks &middot; {sprint?.totalPoints ?? 0} story points
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="sprint-name">Sprint name</Label>
              <Input
                id="sprint-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sprint name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sprint-goal">Sprint goal (optional)</Label>
              <Textarea
                id="sprint-goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="What do you want to achieve?"
                rows={2}
                className="resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Duration</Label>
                <Select value={durationPreset} onValueChange={setDurationPreset}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {durationPreset === 'custom' ? (
                <div className="grid gap-2">
                  <Label htmlFor="custom-days">Days</Label>
                  <Input
                    id="custom-days"
                    type="number"
                    min={1}
                    max={90}
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                  />
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="start-date">Start date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Ends on <span className="font-medium text-foreground">{endDate}</span>
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={startMutation.isPending || !name.trim()}
              className="gap-1.5 cursor-pointer"
            >
              <Play className="h-3.5 w-3.5" />
              {startMutation.isPending ? 'Starting...' : 'Start Sprint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
